(function () {
  if (window.ProductFamilyAjax) return;

  class ProductFamilyAjax {
    constructor() {
      this.sectionSelector = '[data-main-product-section]';
      this.swatchSelector = 'input[data-ajax-product-url], input[data-product-url]';
      this.loadingClass = 'is-loading-product-family';
      this.cache = new Map();

      this.bindEvents();
      this.bindPopState();
      this.initCurrentSection(document);
    }

    getCustomSize() {
      return window.productCustomSize || null;
    }

    bindEvents() {
      document.addEventListener('change', (event) => {
        const input = event.target.closest(this.swatchSelector);
        if (!input) return;

        const url = input.getAttribute('data-ajax-product-url') || input.getAttribute('data-product-url');
        if (!url) return;

        if (input.getAttribute('aria-current') === 'true') return;

        event.preventDefault();
        this.loadProduct(url, true);
      });

      document.addEventListener('mouseover', (event) => {
        const input = event.target.closest(this.swatchSelector);
        if (!input) return;

        const url = input.getAttribute('data-ajax-product-url') || input.getAttribute('data-product-url');
        if (url) this.fetchHtml(url).catch(() => {});
      });

      document.addEventListener('change', (event) => {
        const input = event.target.closest(
          'input[data-custom-size-option], input[data-erp-size-value], select[data-option-name], [data-custom-size-box] select'
        );
        if (!input) return;

        const section = input.closest(this.sectionSelector) || document;
        this.updateOptionLabels(section);
      });
    }

    bindPopState() {
      window.addEventListener('popstate', () => {
        this.loadProduct(window.location.pathname + window.location.search, false);
      });
    }

    initCurrentSection(root = document) {
      const section = root.querySelector
        ? root.querySelector(this.sectionSelector) || root
        : document;

      const customSize = this.getCustomSize();
      if (customSize) customSize.init(section);

      this.updateOptionLabels(section);
      this.initDeliveryTime(section);
    }

    async loadProduct(url, pushState = true) {
      const currentSection = document.querySelector(this.sectionSelector);

      if (!currentSection) {
        window.location.href = url;
        return;
      }

      const selectedOptions = pushState ? this.getSelectedProductOptions(currentSection) : {};

      try {
        document.documentElement.classList.add(this.loadingClass);

        const htmlText = await this.fetchHtml(url);
        const newDoc = new DOMParser().parseFromString(htmlText, 'text/html');
        const newSection = newDoc.querySelector(this.sectionSelector);

        if (!newSection) {
          window.location.href = url;
          return;
        }

        // product-modal is moved to body on connect — remove orphans before section swap
        this.cleanupDetachedProductModals(currentSection);

        currentSection.replaceWith(newSection);

        if (pushState) {
          this.restoreSelectedProductOptions(newSection, selectedOptions);
        }

        this.updateDocumentMeta(newDoc);

        if (pushState) {
          const newUrl = new URL(url, window.location.origin);
          window.history.pushState({}, '', newUrl.pathname + newUrl.search);
        }

        this.afterReplace(newSection);
      } catch (error) {
        console.error('Sibling product switch failed:', error);
        window.location.href = url;
      } finally {
        document.documentElement.classList.remove(this.loadingClass);
      }
    }

    async fetchHtml(url) {
      const absoluteUrl = new URL(url, window.location.origin);
      const cacheKey = absoluteUrl.pathname + absoluteUrl.search;

      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

      const response = await fetch(absoluteUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      this.cache.set(cacheKey, html);

      return html;
    }

    cleanupDetachedProductModals(section) {
      const ids = new Set();
      if (section && section.querySelectorAll) {
        section.querySelectorAll('[data-product-lightbox-open]').forEach((btn) => {
          const modalSel = btn.getAttribute('data-modal');
          if (modalSel) ids.add(modalSel);
        });
        section.querySelectorAll('product-modal[id]').forEach((modal) => {
          ids.add(`#${modal.id}`);
        });
      }

      document.querySelectorAll('body > product-modal.product-media-modal').forEach((modal) => {
        const key = modal.id ? `#${modal.id}` : '';
        if (!key || ids.size === 0 || ids.has(key)) {
          modal.remove();
        }
      });

      if (typeof window.dunteryCleanupProductModals === 'function') {
        window.dunteryCleanupProductModals();
      }
    }

    afterReplace(newSection) {
      this.initCurrentSection(newSection);

      if (typeof window.dunteryCleanupProductModals === 'function') {
        window.dunteryCleanupProductModals();
      }
      if (typeof window.dunteryInitProductZoom === 'function') {
        window.requestAnimationFrame(() => window.dunteryInitProductZoom());
      }

      document.dispatchEvent(
        new CustomEvent('product:ajax-loaded', {
          detail: {
            section: newSection,
            url: window.location.href
          }
        })
      );

      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

      if (typeof window.initializeAfterpay === 'function') {
        window.initializeAfterpay(true);
      }

      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document.title
        });
      }
    }

    getSelectedProductOptions(section) {
      const selected = {};
      const picker = section.querySelector('variant-radios, variant-selects');

      if (!picker) return selected;

      picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        const name = input.getAttribute('name');
        const value = input.value;

        if (!name || !value) return;
        if (this.shouldIgnoreOptionName(name)) return;

        selected[name] = value;
      });

      picker.querySelectorAll('select').forEach((select) => {
        let name = select.getAttribute('name') || select.getAttribute('data-option-name');
        const value = select.value;

        if (!name || !value) return;

        const match = name.match(/^options\[(.*)\]$/);
        if (match && match[1]) name = match[1];

        if (this.shouldIgnoreOptionName(name)) return;

        selected[name] = value;
      });

      return selected;
    }

    restoreSelectedProductOptions(section, selectedOptions) {
      if (!selectedOptions || Object.keys(selectedOptions).length === 0) return;

      const picker = section.querySelector('variant-radios, variant-selects');
      if (!picker) return;

      Object.keys(selectedOptions).forEach((name) => {
        const value = selectedOptions[name];

        const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
        const radio = radios.find((item) => {
          return item.getAttribute('name') === name && item.value === value && !item.disabled;
        });

        if (radio) {
          radio.checked = true;
          return;
        }

        const selects = Array.from(picker.querySelectorAll('select'));
        const select = selects.find((item) => {
          const rawName = item.getAttribute('name') || item.getAttribute('data-option-name') || '';
          const match = rawName.match(/^options\[(.*)\]$/);
          const normalizedName = match && match[1] ? match[1] : rawName;

          return normalizedName === name;
        });

        if (select) {
          const option = Array.from(select.options).find((item) => item.value === value && !item.disabled);
          if (option) select.value = value;
        }
      });

      picker.dispatchEvent(new Event('change', { bubbles: true }));

      const customSize = this.getCustomSize();
      if (customSize) {
        customSize.toggleCustomSizeFields(section);
        customSize.syncErpSizeProperty(section);
      }
      this.updateOptionLabels(section);
    }

    updateOptionLabels(root = document) {
      const container = root.querySelector ? root : document;

      container.querySelectorAll('[data-option-name]:checked').forEach((input) => {
        const optionName = input.getAttribute('data-option-name');
        if (!optionName) return;

        const handle = this.handleize(optionName);
        const label = container.querySelector(`.option-name--${handle}`);

        if (label) label.textContent = input.value;
      });

      container.querySelectorAll('select[data-option-name]').forEach((select) => {
        const optionName = select.getAttribute('data-option-name');
        if (!optionName) return;

        const handle = this.handleize(optionName);
        const label = container.querySelector(`.option-name--${handle}`);

        if (label) label.textContent = select.value;
      });
    }

    initDeliveryTime(root = document) {
      const container = root.querySelector ? root : document;
      const targets = container.querySelectorAll('#faq-delivery-time, .product-ship-box__dates');

      targets.forEach((shipdate) => {
        if (!shipdate.dataset.start || !shipdate.dataset.end) return;

        const minDays = parseInt(shipdate.dataset.start, 10);
        const maxDays = parseInt(shipdate.dataset.end, 10);
        if (Number.isNaN(minDays) || Number.isNaN(maxDays)) return;

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + minDays);
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + maxDays);

        const fmt = (d) => `${months[d.getMonth()]}. ${d.getDate()}`;
        shipdate.textContent =
          minDays === maxDays ? fmt(startDate) : `${fmt(startDate)} - ${fmt(endDate)}`;
      });
    }

    updateDocumentMeta(newDoc) {
      const newTitle = newDoc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;

      [
        'link[rel="canonical"]',
        'meta[name="description"]',
        'meta[property="og:title"]',
        'meta[property="og:url"]',
        'meta[property="og:image"]'
      ].forEach((selector) => {
        const current = document.querySelector(selector);
        const next = newDoc.querySelector(selector);

        if (current && next) current.replaceWith(next);
      });
    }

    shouldIgnoreOptionName(name) {
      const normalized = String(name)
        .toLowerCase()
        .trim()
        .replace(/ß/g, 'ss')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ä/g, 'ae')
        .replace(/ñ/g, 'n');

      return (
        normalized === 'color' ||
        normalized === 'colour' ||
        normalized === 'farbe' ||
        normalized === 'couleur' ||
        normalized === 'colore' ||
        normalized === 'siblingcolor' ||
        normalized === 'sibling color'
      );
    }

    handleize(value) {
      return String(value)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  }

  window.ProductFamilyAjax = ProductFamilyAjax;

  document.addEventListener('DOMContentLoaded', () => {
    if (!window.productFamilyAjax) {
      window.productFamilyAjax = new ProductFamilyAjax();
    }
  });

  if (document.readyState !== 'loading' && !window.productFamilyAjax) {
    window.productFamilyAjax = new ProductFamilyAjax();
  }
})();
